import React, { useState, useEffect, useRef } from 'react';
import { Upload, Image, Video, X, Plus, Save, Star, Trash2, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaUpload = ({ value, onChange, label, token, acceptVideo = false }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value);
  const [mediaType, setMediaType] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(value);
    if (value) {
      const isVideo = value.match(/\.(mp4|webm|mov|avi|mpeg)$/i);
      setMediaType(isVideo ? 'video' : 'image');
    }
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideoFile = file.type.startsWith('video/');

    if (!isImage && !isVideoFile) {
      alert('Please select an image or video file');
      return;
    }

    if (!acceptVideo && isVideoFile) {
      alert('Video uploads are not allowed for this field');
      return;
    }

    // Validate file size (images: 5MB, videos: 100MB)
    const maxSize = isVideoFile ? 100 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File must be less than ${isVideoFile ? '100MB' : '5MB'}`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload-media`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const mediaUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      
      setPreview(mediaUrl);
      setMediaType(response.data.media_type);
      onChange(mediaUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="media-upload-field">
      <label>{label}</label>
      <div className="media-upload-container">
        {preview && (
          <div className="media-preview-small">
            {mediaType === 'video' ? (
              <video src={preview} controls style={{ maxWidth: '100%', maxHeight: '150px' }} />
            ) : (
              <img src={preview} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
            )}
          </div>
        )}
        <div className="media-upload-actions">
          <Input 
            type="text" 
            value={value || ''} 
            onChange={(e) => {
              onChange(e.target.value);
              setPreview(e.target.value);
              const isVideo = e.target.value.match(/\.(mp4|webm|mov|avi|mpeg)$/i);
              setMediaType(isVideo ? 'video' : 'image');
            }}
            placeholder={acceptVideo ? "Enter image/video URL or upload" : "Enter image URL or upload"}
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={acceptVideo ? "image/*,video/*" : "image/*"}
            style={{ display: 'none' }}
          />
          <Button 
            type="button" 
            onClick={() => fileInputRef.current?.click()} 
            className="upload-btn"
            disabled={uploading}
          >
            {acceptVideo ? <Video size={16} /> : <Upload size={16} />}
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Image Upload Component (backward compatible)
const ImageUpload = ({ value, onChange, label, token }) => {
  return <MediaUpload value={value} onChange={onChange} label={label} token={token} acceptVideo={false} />;
};

// Quick Actions Panel Component

const ExperienceForm = ({ experience, onSave, onCancel, isNew, token }) => {
  const [form, setForm] = useState(experience);
  const [featuresText, setFeaturesText] = useState(experience.features?.join('\n') || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    const features = featuresText.split('\n').filter(f => f.trim());
    onSave({ ...form, features });
  };

  const iconOptions = ['Plane', 'Heart', 'Trophy', 'Anchor', 'Sparkles', 'Globe', 'Map', 'Camera', 'Star', 'Sun'];

  return (
    <form onSubmit={handleSubmit} className="edit-form">
      <h4>{isNew ? 'Add New Experience' : 'Edit Experience'}</h4>
      <div className="form-grid">
        <div className="field">
          <label>Title *</label>
          <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
        </div>
        <div className="field">
          <label>Icon</label>
          <Select value={form.icon} onValueChange={(value) => setForm({...form, icon: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {iconOptions.map(icon => (
                <SelectItem key={icon} value={icon}>{icon}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="field full-width">
          <label>Description *</label>
          <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required rows={3} />
        </div>
        <div className="field full-width">
          <ImageUpload 
            label="Image *" 
            value={form.image} 
            onChange={(url) => setForm({...form, image: url})}
            token={token}
          />
        </div>
        <div className="field full-width">
          <label>Instagram Page Link (Optional)</label>
          <Input 
            value={form.instagram_link || ''} 
            onChange={(e) => setForm({...form, instagram_link: e.target.value})} 
            placeholder="https://www.instagram.com/journeysntalesdel/"
            data-testid="experience-instagram-link"
          />
        </div>
        <div className="field">
          <label>Display Order</label>
          <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value)})} />
        </div>
        <div className="field full-width">
          <label>Features (one per line)</label>
          <Textarea value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} rows={4} placeholder="Private guides&#10;Luxury accommodations&#10;Exclusive access" />
        </div>
      </div>
      <div className="form-actions">
        <Button type="submit" className="save-btn"><Save size={16} /> Save</Button>
        <Button type="button" onClick={onCancel} className="cancel-btn"><X size={16} /> Cancel</Button>
      </div>
    </form>
  );
};

// Testimonial Form Component
const TestimonialForm = ({ testimonial, onSave, onCancel, isNew, token }) => {
  const [form, setForm] = useState(testimonial);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form">
      <h4>{isNew ? 'Add New Testimonial' : 'Edit Testimonial'}</h4>
      <div className="form-grid">
        <div className="field">
          <label>Client Name *</label>
          <Input value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} required />
        </div>
        <div className="field">
          <label>Client Title</label>
          <Input value={form.client_title || ''} onChange={(e) => setForm({...form, client_title: e.target.value})} placeholder="e.g., CEO, ABC Corp" />
        </div>
        <div className="field full-width">
          <label>Quote/Testimonial *</label>
          <Textarea value={form.quote} onChange={(e) => setForm({...form, quote: e.target.value})} required rows={4} placeholder="What the client said about their experience..." />
        </div>
        <div className="field full-width">
          <MediaUpload 
            label="Client Photo (Image)" 
            value={form.client_image || ''} 
            onChange={(url) => setForm({...form, client_image: url})}
            token={token}
            acceptVideo={false}
          />
        </div>
        <div className="field full-width">
          <MediaUpload 
            label="Video Testimonial (Optional)" 
            value={form.video_url || ''} 
            onChange={(url) => setForm({...form, video_url: url})}
            token={token}
            acceptVideo={true}
          />
        </div>
        <div className="field">
          <label>Rating (1-5 Stars)</label>
          <Select value={form.rating?.toString()} onValueChange={(value) => setForm({...form, rating: parseInt(value)})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 Stars</SelectItem>
              <SelectItem value="4">4 Stars</SelectItem>
              <SelectItem value="3">3 Stars</SelectItem>
              <SelectItem value="2">2 Stars</SelectItem>
              <SelectItem value="1">1 Star</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="field">
          <label>Destination</label>
          <Input value={form.destination || ''} onChange={(e) => setForm({...form, destination: e.target.value})} placeholder="e.g., Maldives" />
        </div>
        <div className="field">
          <label>Experience Type</label>
          <Input value={form.experience_type || ''} onChange={(e) => setForm({...form, experience_type: e.target.value})} placeholder="e.g., Honeymoon" />
        </div>
        <div className="field">
          <label>Review Source</label>
          <Select value={form.review_source || 'none'} onValueChange={(value) => setForm({...form, review_source: value === 'none' ? null : value})}>
            <SelectTrigger data-testid="review-source-select">
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No External Review</SelectItem>
              <SelectItem value="google">Google Reviews</SelectItem>
              <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="field">
          <label>Review Link</label>
          <Input 
            value={form.review_link || ''} 
            onChange={(e) => setForm({...form, review_link: e.target.value})} 
            placeholder="https://g.page/r/..." 
            disabled={!form.review_source}
            data-testid="review-link-input"
          />
        </div>
        <div className="field">
          <label>Display Order</label>
          <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value)})} />
        </div>
      </div>
      <div className="form-actions">
        <Button type="submit" className="save-btn"><Save size={16} /> Save</Button>
        <Button type="button" onClick={onCancel} className="cancel-btn"><X size={16} /> Cancel</Button>
      </div>
    </form>
  );
};

// Gallery Item Form Component
const GalleryItemForm = ({ item, onSave, onCancel, isNew, token }) => {
  const [form, setForm] = useState(item);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form gallery-form">
      <h4>{isNew ? 'Add New Gallery Item' : 'Edit Gallery Item'}</h4>
      <div className="form-grid">
        <div className="field">
          <label>Media Type</label>
          <Select value={form.media_type} onValueChange={(value) => setForm({...form, media_type: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="video">Video</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="field">
          <label>Destination</label>
          <Input 
            value={form.destination || ''} 
            onChange={(e) => setForm({...form, destination: e.target.value})} 
            placeholder="e.g., Santorini, Greece"
          />
        </div>
        <div className="field full-width">
          <MediaUpload 
            label={form.media_type === 'video' ? 'Video *' : 'Image *'}
            value={form.media_url} 
            onChange={(url) => setForm({...form, media_url: url})}
            token={token}
            acceptVideo={form.media_type === 'video'}
          />
        </div>
        <div className="field full-width">
          <label>Caption (Optional)</label>
          <Textarea 
            value={form.caption || ''} 
            onChange={(e) => setForm({...form, caption: e.target.value})} 
            rows={2}
            placeholder="Brief description of the moment..."
          />
        </div>
        <div className="field">
          <label>Display Order</label>
          <Input 
            type="number" 
            value={form.order} 
            onChange={(e) => setForm({...form, order: parseInt(e.target.value)})} 
          />
        </div>
      </div>
      <div className="form-actions">
        <Button type="submit" className="save-btn"><Save size={16} /> Save</Button>
        <Button type="button" onClick={onCancel} className="cancel-btn"><X size={16} /> Cancel</Button>
      </div>
    </form>
  );
};

export { MediaUpload, ImageUpload, ExperienceForm, TestimonialForm, GalleryItemForm };
